import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import {
  User, MapPin, Calendar, Crown, Heart, Star, Camera, 
  FileText, Music, Globe, Lock, Users, MessageSquare,
  UserPlus, UserCheck, Settings, Edit, Upload, Badge as BadgeIcon,
  Flag, Download, ZoomIn, Scroll, Languages, TreePine, 
  Mountain, Sparkles, Shield, Feather, BookOpen, Share,
  Send, Trash2, MoreVertical
} from 'lucide-react';

// Import UI components
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

// Import services  
import { publicProfileAPI, tribalSocialAPI } from '../services/api';
import FollowButton from '../components/FollowButton';
import PeacePipeMessage from '../components/PeacePipeMessage';
import Header from '../components/Header';

const PublicProfile = () => {
  const { userId } = useParams();
  const { user: currentUser, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [cosmicConnection, setCosmicConnection] = useState(null);
  const [cosmicDetailedExplanations, setCosmicDetailedExplanations] = useState(null);
  const [generatingDetails, setGeneratingDetails] = useState(false);
  
  // Media interactions state
  const [photoInteractions, setPhotoInteractions] = useState({});
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  // Gallery functionality removed per user request

  useEffect(() => {
    // Allow loading profile even without authentication for public viewing
    loadProfile();
    loadCosmicConnection();
  }, [userId]);
  
  // DEBUG: Log when profile state changes
  useEffect(() => {
    if (profile) {
      console.log('🔄 Profile state updated:', {
        userId: profile.user_id,
        coverImage: profile.cover_image,
        fullName: profile.full_name
      });
    }
  }, [profile]);

  // Gallery functionality removed per user request

  const loadCosmicConnection = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${BACKEND_URL}/api/cosmic-codes/profile-connection/${userId}`);
      const data = await response.json();
      
      if (data.success && data.cosmic_connection) {
        setCosmicConnection(data.cosmic_connection);
      }
    } catch (error) {
      console.error('Error loading cosmic connection:', error);
    }
  };

  const generateDetailedExplanations = async () => {
    try {
      setGeneratingDetails(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const response = await fetch(`${BACKEND_URL}/api/cosmic-codes/generate-detailed-explanations/${userId}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setCosmicDetailedExplanations(data.detailed_explanations);
        // Also reload profile to get saved data
        await loadProfile();
        toast({
          title: "Cosmic Insights Revealed",
          description: "AI-powered interpretations have been generated for each element!"
        });
      } else {
        throw new Error('Failed to generate detailed explanations');
      }
    } catch (error) {
      console.error('Error generating detailed explanations:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to generate detailed cosmic insights. Please try again."
      });
    } finally {
      setGeneratingDetails(false);
    }
  };

  const loadProfile = async () => {
    try {
      setLoading(true);
      const profileData = await publicProfileAPI.getProfile(userId);
      
      // DEBUG: Log the profile data
      console.log('🔍 Profile loaded:', {
        userId: profileData.user_id,
        coverImage: profileData.cover_image,
        profilePicture: profileData.profile_picture
      });
      
      setProfile(profileData);
      setIsFollowing(profileData.is_following);
      setIsOwnProfile(currentUser && currentUser.id === userId);
      
      // Load detailed cosmic explanations if exists
      if (profileData.cosmic_detailed_explanations) {
        setCosmicDetailedExplanations(profileData.cosmic_detailed_explanations);
      }
      
      // Load interactions for media photos if user is authenticated
      if (isAuthenticated && profileData.media_photos && profileData.media_photos.length > 0) {
        await loadPhotoInteractions(profileData.media_photos);
      }
      
      // Gallery functionality removed per user request
    } catch (error) {
      console.error('Error loading profile:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load profile"
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to safely get interpretation text - FULL DISPLAY (no truncation)
  const getInterpretationText = (interpretation) => {
    if (!interpretation) return '';
    
    // If it's already a string, return it fully
    if (typeof interpretation === 'string') {
      return interpretation;
    }
    
    // If it's an object with detailed fields (from new save format)
    if (typeof interpretation === 'object') {
      // Check if it has the detailed structure (meaning, tribal_archetype, etc.)
      if (interpretation.meaning || interpretation.tribal_archetype) {
        const parts = [];
        if (interpretation.meaning) parts.push(interpretation.meaning);
        if (interpretation.tribal_archetype) parts.push(`\n\n🪶 Tribal Archetype: ${interpretation.tribal_archetype}`);
        if (interpretation.ancestral_connection) parts.push(`\n\n🌿 Ancestral Connection: ${interpretation.ancestral_connection}`);
        if (interpretation.personal_growth) parts.push(`\n\n✨ Personal Growth: ${interpretation.personal_growth}`);
        
        return parts.join('');
      }
      
      // Legacy format: object with an interpretation property
      if (interpretation.interpretation) {
        return interpretation.interpretation;
      }
    }
    
    // Fallback
    return '';
  };

  const loadPhotoInteractions = async (photoUrls) => {
    if (!isAuthenticated) return;
    
    const interactions = {};
    
    for (const photoUrl of photoUrls) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media-interactions/photo/${encodeURIComponent(photoUrl)}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          interactions[photoUrl] = data;
        }
      } catch (error) {
        console.error('Error loading interactions for photo:', error);
      }
    }
    
    setPhotoInteractions(interactions);
  };

  // Cover photo upload function removed

  const handleLikePhoto = async (photoUrl) => {
    if (!isAuthenticated) {
      toast({
        title: "Sign in required",
        description: "Please sign in to like photos",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media-interactions/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          photo_url: photoUrl,
          photo_owner_id: userId
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // Update local state
        setPhotoInteractions(prev => ({
          ...prev,
          [photoUrl]: {
            ...prev[photoUrl],
            user_liked: result.liked,
            likes_count: result.liked 
              ? (prev[photoUrl]?.likes_count || 0) + 1 
              : Math.max(0, (prev[photoUrl]?.likes_count || 1) - 1)
          }
        }));

        toast({
          title: result.liked ? "Photo liked!" : "Photo unliked",
          description: result.message
        });
      } else {
        throw new Error('Failed to toggle like');
      }
    } catch (error) {
      console.error('Error liking photo:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to like photo. Please try again."
      });
    }
  };

  const handleAddComment = async (photoUrl) => {
    if (!isAuthenticated) {
      toast({
        title: "Sign in required",
        description: "Please sign in to comment",
        variant: "destructive"
      });
      return;
    }

    if (!commentText.trim()) {
      toast({
        variant: "destructive",
        title: "Empty comment",
        description: "Please enter a comment"
      });
      return;
    }

    setSubmittingComment(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media-interactions/comment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          photo_url: photoUrl,
          photo_owner_id: userId,
          comment_text: commentText.trim()
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // Update local state
        setPhotoInteractions(prev => ({
          ...prev,
          [photoUrl]: {
            ...prev[photoUrl],
            comments_count: (prev[photoUrl]?.comments_count || 0) + 1,
            recent_comments: [result.comment, ...(prev[photoUrl]?.recent_comments || [])]
          }
        }));

        setCommentText('');
        
        toast({
          title: "Comment added!",
          description: result.message
        });
      } else {
        throw new Error('Failed to add comment');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add comment. Please try again."
      });
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId, photoUrl) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media-interactions/comment/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        // Update local state
        setPhotoInteractions(prev => ({
          ...prev,
          [photoUrl]: {
            ...prev[photoUrl],
            comments_count: Math.max(0, (prev[photoUrl]?.comments_count || 1) - 1),
            recent_comments: (prev[photoUrl]?.recent_comments || []).filter(comment => comment.id !== commentId)
          }
        }));

        toast({
          title: "Comment deleted",
          description: "Comment has been removed"
        });
      } else {
        throw new Error('Failed to delete comment');
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete comment. Please try again."
      });
    }
  };

  // Follow functionality now handled by FollowButton component

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center mt-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center mt-20">
          <div className="text-center">
            <User className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Profile not found</h3>
            <Button onClick={() => navigate(-1)}>Go Back</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Own Profile Banner */}
      {isOwnProfile && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2 sm:py-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
              <div className="flex items-center gap-2 sm:gap-3">
                <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
                <div>
                  <p className="text-xs sm:text-sm font-medium text-blue-900">
                    This is your public profile
                  </p>
                  <p className="text-xs text-blue-700 hidden sm:block">
                    This is how other community members see your profile
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate('/profile')}
                className="border-blue-300 text-blue-700 hover:bg-blue-100 w-full sm:w-auto text-xs sm:text-sm"
              >
                <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Edit Profile
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 🔱 1. Hero Banner (Top Section) - Fixed Layout */}
      <div className="relative">
        {/* Background Cover Image - Fixed Default for All Users */}
        <div 
          className="h-[350px] sm:h-[400px] md:h-[500px] relative overflow-hidden"
          style={{
            backgroundImage: `url(/default-cover.png)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* Removed: Upload Cover Photo Button - Cover is now fixed for all users */}

          {/* Overlay pattern for tribal aesthetic */}
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black opacity-40"></div>
          
          {/* Tribal pattern overlay */}
          <div className="absolute inset-0 opacity-10">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="tribal" patternUnits="userSpaceOnUse" width="40" height="40">
                  <polygon points="20,1 40,39 1,39" fill="white" opacity="0.3"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#tribal)" />
            </svg>
          </div>

          {/* Profile Content - Properly Sized and Positioned */}
          <div className="absolute inset-0 flex items-center justify-center py-6 sm:py-8 md:py-12">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 w-full">
              <div className="flex flex-col items-center text-white space-y-3 sm:space-y-4">
                
                {/* Profile Picture - Responsive size */}
                <div className="relative flex-shrink-0 mt-4 sm:mt-6 md:mt-8">
                  {/* Gradient border - Reduced size to prevent cutoff */}
                  <div className="relative p-1 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-full">
                    <Avatar className="h-24 w-24 sm:h-28 sm:w-28 md:h-32 md:w-32 border-3 sm:border-4 border-white shadow-2xl">
                      <AvatarImage 
                        src={profile.profile_picture} 
                        alt={`${profile.tribal_name || profile.full_name}'s profile`}
                        className="object-cover"
                      />
                      <AvatarFallback className="text-2xl sm:text-3xl bg-gray-100 text-gray-600 font-bold">
                        {profile.tribal_name?.split(' ').map(n => n[0]).join('') || 
                         profile.full_name?.split(' ').map(n => n[0]).join('') || 
                         '👤'}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  
                  {/* Family Crest Badge - Adjusted position */}
                  {profile.family_crest && (
                    <div className="absolute -bottom-2 -right-2 bg-white rounded-full p-1 shadow-lg">
                      <Dialog>
                        <DialogTrigger asChild>
                          <div className="cursor-pointer">
                            <img 
                              src={profile.family_crest} 
                              alt="Family Crest" 
                              className="h-10 w-10 rounded-full border-2 border-amber-300 hover:border-amber-400 transition-colors"
                            />
                          </div>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle className="flex items-center">
                              <Shield className="w-5 h-5 mr-2" />
                              Family Crest
                            </DialogTitle>
                          </DialogHeader>
                          <div className="text-center space-y-4">
                            <img 
                              src={profile.family_crest} 
                              alt="Family Crest Full View" 
                              className="w-48 h-48 mx-auto rounded-lg border-4 border-amber-200"
                            />
                            <Button variant="outline" className="w-full">
                              <Download className="w-4 h-4 mr-2" />
                              Download Full Resolution
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}
                </div>

                {/* Name & Info - Properly Spaced */}
                <div className="text-center space-y-2 sm:space-y-3 max-w-2xl px-2">
                  {/* Name Section */}
                  <div className="space-y-1">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold drop-shadow-lg leading-tight">
                      {profile.tribal_name || profile.full_name}
                    </h1>
                    
                    <p className="text-sm sm:text-base md:text-lg text-amber-200">
                      @{profile.username || 
                        (profile.tribal_name && profile.tribal_name.toLowerCase().replace(/\s+/g, '.')) ||
                        'member'
                      }
                    </p>
                  </div>

                  {/* Bio Section - Compact */}
                  {profile.bio && (
                    <div className="mt-2">
                      <p className="text-sm sm:text-base text-white/90 leading-relaxed line-clamp-2">
                        {profile.bio}
                      </p>
                    </div>
                  )}

                  {/* Location Info - Compact */}
                  <div className="space-y-2 mt-2 sm:mt-3">
                    <div className="flex items-center justify-center gap-2">
                      <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-amber-300" />
                      <span className="text-xs sm:text-sm md:text-base font-medium">
                        {profile.region_flag || 'Region Unknown – Tribal Territory'}
                      </span>
                    </div>

                    {/* Heritage Flags - Compact */}
                    <div className="flex items-center justify-center gap-3 flex-wrap">
                      {profile.ancestral_flags && profile.ancestral_flags.map((flag, index) => (
                        <div key={index} className="flex items-center gap-1">
                          <Flag className="w-3 h-3 text-amber-300" />
                          <span className="text-xs">{flag}</span>
                        </div>
                      ))}
                      {!profile.ancestral_flags && (
                        <div className="flex items-center gap-1">
                          <Feather className="w-3 h-3 text-amber-300" />
                          <span className="text-xs">Indigenous Heritage</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Stats Row - Positioned to Stay Within Banner */}
                  <div className="flex items-center justify-center gap-3 sm:gap-4 md:gap-6 text-xs sm:text-sm py-2 sm:py-3 mt-2 sm:mt-4 mb-2 sm:mb-4">
                    <div className="flex items-center gap-1">
                      <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="font-medium">{profile.followers_count || 0} followers</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Heart className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="font-medium">{profile.following_count || 0} following</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="font-medium">{profile.posts_count || 0} posts</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Action Buttons - Separate Section Below Hero */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
            <div className="flex justify-center lg:justify-start">
              {!isOwnProfile && isAuthenticated && (
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
                  <FollowButton
                    userId={userId}
                    initialFollowStatus={isFollowing}
                    size="default"
                    variant="default"
                    onFollowChange={(newStatus) => setIsFollowing(newStatus)}
                    className="bg-amber-600 hover:bg-amber-700 text-white border-none px-6 py-2 w-full sm:w-auto"
                  />
                  <PeacePipeMessage
                    receiverId={userId}
                    receiverName={profile.tribal_name || profile.full_name}
                    trigger={
                      <Button variant="outline" className="bg-white text-gray-800 hover:bg-gray-50 border-gray-300 px-6 py-2 w-full sm:w-auto">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Send Message
                      </Button>
                    }
                  />
                </div>
              )}

              {!isOwnProfile && !isAuthenticated && (
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
                  <Button 
                    variant="default" 
                    className="bg-amber-600 hover:bg-amber-700 text-white border-none px-6 py-2 w-full sm:w-auto"
                    onClick={() => navigate('/')}
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Sign In to Follow
                  </Button>
                  <Button 
                    variant="outline" 
                    className="bg-white text-gray-800 hover:bg-gray-50 border-gray-300 px-6 py-2 w-full sm:w-auto"
                    onClick={() => navigate('/')}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Sign In to Message
                  </Button>
                </div>
              )}
              
              {isOwnProfile && (
                <div className="flex gap-4">
                  <Button
                    onClick={() => navigate('/profile')}
                    variant="outline"
                    className="bg-white text-gray-800 hover:bg-gray-50 border-gray-300 px-6 py-2"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <Tabs defaultValue="story" className="w-full">
          <TabsList className={`flex w-full mb-4 sm:mb-8 overflow-x-auto ${
            // Show 4 columns if Heritage/Lineage is accessible, otherwise 3 columns
            (() => {
              const subscription_tier = profile.user_subscription_tier || 'free';
              const user_role = currentUser?.role || 'user';
              const canAccessLineage = subscription_tier === 'seekers' || subscription_tier === 'crown' || user_role === 'admin';
              return canAccessLineage ? 'sm:grid sm:grid-cols-4' : 'sm:grid sm:grid-cols-3';
            })()
          }`}>
            <TabsTrigger value="story" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4">
              <BookOpen className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">My </span>Story
            </TabsTrigger>
            
            {/* Heritage/Lineage Tab - Only for Admins and Paid Users */}
            {(() => {
              const subscription_tier = profile.user_subscription_tier || 'free';
              const user_role = currentUser?.role || 'user';
              const canAccessLineage = subscription_tier === 'seekers' || subscription_tier === 'crown' || user_role === 'admin';
              
              return canAccessLineage && (
                <TabsTrigger value="lineage" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4">
                  <Scroll className="w-3 h-3 sm:w-4 sm:h-4" />
                  Heritage
                </TabsTrigger>
              );
            })()}
            
            <TabsTrigger value="crest" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4">
              <Shield className="w-3 h-3 sm:w-4 sm:h-4" />
              Crest
            </TabsTrigger>

            <TabsTrigger value="cosmic" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4">
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
              Cosmic
            </TabsTrigger>
          </TabsList>

          {/* 📖 1. My Story Section (Now First) */}
          <TabsContent value="story" className="space-y-6">
            {/* Bio Story */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BookOpen className="w-5 h-5 mr-2" />
                  {isOwnProfile ? 'My Story' : 'Their Story'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {profile.bio_story ? (
                  <div className="prose max-w-none">
                    <p className="text-gray-700 leading-relaxed text-lg">{profile.bio_story}</p>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">
                      {isOwnProfile 
                        ? 'Share your story by editing your profile'
                        : 'This member hasn\'t shared their story yet'
                      }
                    </p>
                    {isOwnProfile && (
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => navigate('/profile')}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Profile
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Social Media Links */}
            {(profile.instagram_handle || profile.twitter_handle || profile.youtube_channel || profile.facebook_url || profile.twitch_url) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Globe className="w-5 h-5 mr-2" />
                    Connect Online
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4">
                    {profile.instagram_handle && (
                      <a
                        href={`https://instagram.com/${profile.instagram_handle.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200 hover:scale-105"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                          <path d="m16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                        </svg>
                        <span className="font-medium">@{profile.instagram_handle.replace('@', '')}</span>
                      </a>
                    )}

                    {profile.twitter_handle && (
                      <a
                        href={`https://twitter.com/${profile.twitter_handle.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-all duration-200 hover:scale-105"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
                        </svg>
                        <span className="font-medium">@{profile.twitter_handle.replace('@', '')}</span>
                      </a>
                    )}

                    {profile.youtube_channel && (
                      <a
                        href={profile.youtube_channel.startsWith('http') ? profile.youtube_channel : `https://${profile.youtube_channel}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 hover:scale-105"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/>
                          <polygon points="9.75,15.02 15.5,11.75 9.75,8.48" fill="white"/>
                        </svg>
                        <span className="font-medium">YouTube Channel</span>
                      </a>
                    )}

                    {profile.facebook_url && (
                      <a
                        href={profile.facebook_url.startsWith('http') ? profile.facebook_url : `https://${profile.facebook_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 hover:scale-105"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                        <span className="font-medium">Facebook</span>
                      </a>
                    )}

                    {profile.twitch_url && (
                      <a
                        href={profile.twitch_url.startsWith('http') ? profile.twitch_url : `https://${profile.twitch_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all duration-200 hover:scale-105"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
                        </svg>
                        <span className="font-medium">Twitch</span>
                      </a>
                    )}
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-3 flex items-center">
                    <Share className="w-3 h-3 mr-1" />
                    Follow their cultural journey and content across social platforms
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Cultural Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {profile.cultural_practices && profile.cultural_practices.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center">
                      <Feather className="w-5 h-5 mr-2 text-amber-600" />
                      Cultural Practices
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {profile.cultural_practices.map((practice, index) => (
                        <Badge key={index} variant="secondary" className="bg-amber-100 text-amber-800">
                          {practice}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {profile.spiritual_totems && profile.spiritual_totems.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center">
                      <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
                      Spiritual Totems
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {profile.spiritual_totems.map((totem, index) => (
                        <Badge key={index} variant="outline" className="border-purple-300 text-purple-700">
                          {totem}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {profile.traditional_dances && profile.traditional_dances.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center">
                      <Music className="w-5 h-5 mr-2 text-red-600" />
                      Traditional Dances
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {profile.traditional_dances.map((dance, index) => (
                        <Badge key={index} variant="secondary" className="bg-red-100 text-red-800">
                          {dance}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {profile.favorite_foods && profile.favorite_foods.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center">
                      <Crown className="w-5 h-5 mr-2 text-green-600" />
                      Traditional Foods
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {profile.favorite_foods.map((food, index) => (
                        <Badge key={index} variant="secondary" className="bg-green-100 text-green-800">
                          {food}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Media Photos Section with Comments and Likes */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Camera className="w-5 h-5 mr-2 text-blue-600" />
                  Media Photos
                </CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  {isOwnProfile ? 'Your shared photos' : `${profile.tribal_name || 'Their'} shared photos`}
                </p>
              </CardHeader>
              {profile.media_photos && profile.media_photos.length > 0 ? (
                <CardContent>
                  <div className="space-y-8">
                    {profile.media_photos.map((photoUrl, index) => {
                      const photoData = photoInteractions[photoUrl] || {};
                      const isString = typeof photoUrl === 'string';
                      const imageUrl = isString ? photoUrl : photoUrl.url;
                      const filename = isString ? `Photo ${index + 1}` : photoUrl.filename;
                      
                      return (
                        <div key={index} className="border rounded-lg overflow-hidden bg-white shadow-sm">
                          {/* Photo Display */}
                          <Dialog>
                            <DialogTrigger asChild>
                              <div className="relative group cursor-pointer">
                                <img
                                  src={imageUrl}
                                  alt={filename}
                                  className="w-full max-h-96 object-cover group-hover:opacity-95 transition-opacity"
                                  onError={(e) => {
                                    e.target.src = '/api/placeholder/400/300';
                                  }}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 flex items-center justify-center">
                                  <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                                </div>
                              </div>
                            </DialogTrigger>
                            
                            {/* Full-size photo modal */}
                            <DialogContent className="max-w-6xl w-full">
                              <DialogHeader>
                                <DialogTitle>
                                  {filename}
                                </DialogTitle>
                              </DialogHeader>
                              <div className="flex items-center justify-center max-h-[80vh]">
                                <img
                                  src={imageUrl}
                                  alt={filename}
                                  className="max-h-full max-w-full object-contain rounded-lg"
                                  onError={(e) => {
                                    e.target.src = '/api/placeholder/600/400';
                                  }}
                                />
                              </div>
                            </DialogContent>
                          </Dialog>
                          
                          {/* Interaction Panel */}
                          {isAuthenticated && (
                            <div className="p-4 space-y-3">
                              {/* Like and Comment Buttons */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleLikePhoto(imageUrl)}
                                    className={`flex items-center gap-2 ${
                                      photoData.user_liked 
                                        ? 'text-red-600 hover:text-red-700' 
                                        : 'text-gray-600 hover:text-red-600'
                                    }`}
                                  >
                                    <Heart 
                                      className={`w-5 h-5 ${
                                        photoData.user_liked ? 'fill-current' : ''
                                      }`} 
                                    />
                                    <span className="text-sm">
                                      {photoData.likes_count || 0}
                                    </span>
                                  </Button>
                                  
                                  <div className="flex items-center gap-2 text-gray-600">
                                    <MessageSquare className="w-5 h-5" />
                                    <span className="text-sm">
                                      {photoData.comments_count || 0} comment{(photoData.comments_count || 0) !== 1 ? 's' : ''}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Recent Comments */}
                              {photoData.recent_comments && photoData.recent_comments.length > 0 && (
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                  {photoData.recent_comments.slice(0, 3).map((comment) => (
                                    <div key={comment.id} className="flex items-start gap-3 p-2 bg-gray-50 rounded-lg">
                                      <Avatar className="h-8 w-8 flex-shrink-0">
                                        <AvatarImage 
                                          src={comment.commenter_avatar} 
                                          alt={comment.commenter_name}
                                        />
                                        <AvatarFallback className="text-xs">
                                          {comment.commenter_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                        </AvatarFallback>
                                      </Avatar>
                                      
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between">
                                          <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-900">
                                              {comment.commenter_name}
                                            </p>
                                            <p className="text-sm text-gray-700 mt-1 break-words">
                                              {comment.comment_text}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">
                                              {new Date(comment.created_at).toLocaleDateString()}
                                            </p>
                                          </div>
                                          
                                          {/* Delete button for comment author or photo owner */}
                                          {(currentUser && (
                                            comment.commenter_id === currentUser.id || 
                                            isOwnProfile
                                          )) && (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => handleDeleteComment(comment.id, imageUrl)}
                                              className="text-gray-400 hover:text-red-600 p-1"
                                            >
                                              <Trash2 className="w-3 h-3" />
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {/* Add Comment Form */}
                              <div className="flex items-center gap-2">
                                <Avatar className="h-8 w-8 flex-shrink-0">
                                  <AvatarImage 
                                    src={currentUser?.profile_picture} 
                                    alt="Your profile"
                                  />
                                  <AvatarFallback className="text-xs">
                                    {currentUser?.tribal_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'You'}
                                  </AvatarFallback>
                                </Avatar>
                                
                                <div className="flex-1 flex gap-2">
                                  <Input
                                    placeholder="Add a comment..."
                                    value={selectedPhoto === imageUrl ? commentText : ''}
                                    onChange={(e) => {
                                      setCommentText(e.target.value);
                                      setSelectedPhoto(imageUrl);
                                    }}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        if (selectedPhoto === imageUrl) {
                                          handleAddComment(imageUrl);
                                        }
                                      }
                                    }}
                                    className="text-sm"
                                    disabled={submittingComment}
                                  />
                                  
                                  <Button
                                    size="sm"
                                    onClick={() => handleAddComment(imageUrl)}
                                    disabled={submittingComment || (selectedPhoto === imageUrl && !commentText.trim())}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-3"
                                  >
                                    <Send className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Sign in prompt for non-authenticated users */}
                          {!isAuthenticated && (
                            <div className="p-4 bg-gray-50 text-center">
                              <p className="text-sm text-gray-600 mb-2">
                                Sign in to like and comment on photos
                              </p>
                              <Button 
                                size="sm" 
                                onClick={() => navigate('/')}
                                className="bg-amber-600 hover:bg-amber-700 text-white"
                              >
                                Sign In
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="mt-6 text-center">
                    <p className="text-sm text-gray-500">
                      {profile.media_photos.length} photo{profile.media_photos.length !== 1 ? 's' : ''} shared
                    </p>
                  </div>
                </CardContent>
              ) : (
                <CardContent className="text-center py-12">
                  <Camera className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">
                    {isOwnProfile ? 'No photos uploaded yet' : 'No photos shared yet'}
                  </p>
                  {isOwnProfile && (
                    <p className="text-sm text-gray-400">
                      Click "Upload Photo" above to add your first photo
                    </p>
                  )}
                </CardContent>
              )}
            </Card>
          </TabsContent>

          {/* 🪶 2. Heritage/Lineage Section - Only for Admins and Paid Users */}
          {(() => {
            const subscription_tier = profile.user_subscription_tier || 'free';
            const user_role = currentUser?.role || 'user';
            const canAccessLineage = subscription_tier === 'seekers' || subscription_tier === 'crown' || user_role === 'admin';
            
            return canAccessLineage && (
              <TabsContent value="lineage" className="space-y-6">
                {/* Check subscription tier and saved analysis */}
                {(() => {
                  const hasSubscription = profile.user_subscription_tier && 
                                         (profile.user_subscription_tier === 'seekers' || profile.user_subscription_tier === 'crown');
                  const hasSavedAnalysis = (profile.known_tribes && profile.known_tribes.length > 0) ||
                                           (profile.surname_heritage && profile.surname_heritage.length > 0) ||
                                           (profile.heritage_analysis && profile.heritage_analysis.surname_results && profile.heritage_analysis.surname_results.length > 0);
                  
                  // Admin viewing: always show the lineage section (even if empty)
                  const isAdminViewing = user_role === 'admin';
              
              // Free user empty state (but show to admins for layout preview)
              if (!hasSubscription && !isAdminViewing) {
                return (
                  <Card className="relative overflow-hidden border-2 border-amber-200">
                    <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-amber-400 via-orange-400 to-red-400"></div>
                    <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
                      <CardTitle className="flex items-center text-2xl text-amber-900">
                        <Scroll className="w-6 h-6 mr-3" />
                        📜 Ancestral Lineage Record
                      </CardTitle>
                      <p className="text-amber-700">Documented tribal heritage and family connections</p>
                    </CardHeader>
                    <CardContent className="py-12 text-center bg-gradient-to-b from-amber-25 to-white">
                      <div className="max-w-md mx-auto space-y-4">
                        <div className="text-6xl mb-4">🔒</div>
                        <h3 className="text-xl font-bold text-gray-900">Heritage Analysis Required</h3>
                        <p className="text-gray-600">
                          This section displays your saved ancestral lineage analysis results.
                        </p>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-6">
                          <p className="text-sm text-amber-800">
                            <strong>🌟 Available for Seekers Path & Crown Subscribers</strong>
                          </p>
                          <p className="text-sm text-amber-700 mt-2">
                            Complete a Tribal or Surname Genealogy Analysis and save it to your profile to display your ancestral heritage here.
                          </p>
                        </div>
                        <Button 
                          className="mt-6 bg-amber-600 hover:bg-amber-700 text-white"
                          onClick={() => window.location.href = '/subscription'}
                        >
                          <Crown className="w-4 h-4 mr-2" />
                          Upgrade to View Heritage
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              }
              
              // Paid user but no saved analysis (or admin viewing empty profile)
              if (!hasSavedAnalysis) {
                // If admin viewing, show a note that they can view the layout
                const adminNote = isAdminViewing ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                    <p className="text-xs text-purple-800">
                      <strong>👑 Admin View:</strong> This profile has no saved lineage data. 
                      You're viewing this tab because you're an admin.
                    </p>
                  </div>
                ) : null;
                
                return (
                  <Card className="relative overflow-hidden border-2 border-amber-200">
                    <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-amber-400 via-orange-400 to-red-400"></div>
                    <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
                      <CardTitle className="flex items-center text-2xl text-amber-900">
                        <Scroll className="w-6 h-6 mr-3" />
                        📜 Ancestral Lineage Record
                      </CardTitle>
                      <p className="text-amber-700">Documented tribal heritage and family connections</p>
                    </CardHeader>
                    <CardContent className="py-12 text-center bg-gradient-to-b from-amber-25 to-white">
                      {adminNote}
                      <div className="max-w-md mx-auto space-y-4">
                        <div className="text-6xl mb-4">📋</div>
                        <h3 className="text-xl font-bold text-gray-900">No Lineage Analysis Saved Yet</h3>
                        <p className="text-gray-600">
                          Complete a genealogy analysis and click "Save to Profile" to display your ancestral heritage here.
                        </p>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                          <p className="text-sm text-blue-800">
                            <strong>💡 How to add your lineage:</strong>
                          </p>
                          <ol className="text-sm text-blue-700 mt-2 text-left space-y-1">
                            <li>1. Go to "AI Ancestry Analysis" from the menu</li>
                            <li>2. Complete a Tribal or Surname Genealogy Analysis</li>
                            <li>3. Click "Save to Profile" on your results page</li>
                            <li>4. Your heritage will appear here!</li>
                          </ol>
                        </div>
                        <Button 
                          className="mt-6 bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => navigate('/lineage')}
                        >
                          <Scroll className="w-4 h-4 mr-2" />
                          Start Genealogy Analysis
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              }
              
              // Paid user with saved analysis - show real data
              return (
                <Card className="relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-amber-400 via-orange-400 to-red-400"></div>
                  <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
                    <CardTitle className="flex items-center text-2xl text-amber-900">
                      <Scroll className="w-6 h-6 mr-3" />
                      📜 Ancestral Lineage Record
                    </CardTitle>
                    <p className="text-amber-700">Documented tribal heritage and family connections</p>
                  </CardHeader>
                  <CardContent className="space-y-6 bg-gradient-to-b from-amber-25 to-white">
                    
                    {/* Surnames */}
                    {profile.surname_heritage && profile.surname_heritage.length > 0 && (
                      <div className="border-l-4 border-amber-400 pl-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Surname Heritage</h3>
                        <div className="flex flex-wrap gap-3">
                          {profile.surname_heritage.map((surname, index) => (
                            <div key={index} className="bg-amber-100 border border-amber-300 rounded-lg px-4 py-3 shadow-sm">
                              <div className="font-bold text-amber-900 text-lg">{surname.surname}</div>
                              <div className="text-sm text-amber-700">{surname.origin_country}</div>
                              {surname.meaning && <div className="text-xs text-amber-600 italic mt-1">{surname.meaning}</div>}
                              {surname.tribal_connection && (
                                <div className="text-xs text-amber-800 mt-2 bg-amber-50 px-2 py-1 rounded">
                                  🪶 {surname.tribal_connection}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Heritage Analysis Results */}
                    {profile.heritage_analysis && profile.heritage_analysis.surname_results && profile.heritage_analysis.surname_results.length > 0 && (
                      <div className="border-l-4 border-purple-400 pl-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                          <Shield className="w-5 h-5 mr-2 text-purple-600" />
                          Ancestral Heritage Analysis
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                          Analysis completed on {new Date(profile.heritage_analysis.analysis_date).toLocaleDateString()}
                        </p>
                        <div className="space-y-4">
                          {profile.heritage_analysis.surname_results.map((result, index) => (
                            <div key={index} className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <h4 className="font-bold text-purple-900 text-lg">{result.surname}</h4>
                                  <p className="text-sm text-purple-700">{result.country_origin}</p>
                                </div>
                              </div>
                              {result.etymology && (
                                <p className="text-sm text-gray-700 mt-2">
                                  <strong>Etymology:</strong> {result.etymology}
                                </p>
                              )}
                              {result.germanic_tribe && result.germanic_tribe.tribe_name && (
                                <div className="mt-3 bg-purple-100 border border-purple-300 rounded p-3">
                                  <p className="font-semibold text-purple-900 flex items-center">
                                    <Shield className="w-4 h-4 mr-2" />
                                    Germanic Tribe: {result.germanic_tribe.tribe_name}
                                  </p>
                                  {result.germanic_tribe.tribe_history && (
                                    <p className="text-xs text-purple-800 mt-2">
                                      {result.germanic_tribe.tribe_history}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tribal Affiliations */}
                    {profile.known_tribes && profile.known_tribes.length > 0 && (
                      <div className="border-l-4 border-orange-400 pl-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Tribal Affiliations</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {profile.known_tribes.map((tribe, index) => (
                            <div key={index} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-3">
                                <div>
                                  <h4 className="font-bold text-orange-900 text-lg">{tribe.tribe_name}</h4>
                                  <p className="text-sm text-orange-700">{tribe.region}</p>
                                  {tribe.time_period && <p className="text-xs text-orange-600">{tribe.time_period}</p>}
                                </div>
                                <Badge className="bg-orange-200 text-orange-800">
                                  {Math.round(tribe.confidence_level * 100)}%
                                </Badge>
                              </div>
                              {tribe.language_family && (
                                <p className="text-xs text-orange-600 mt-2">
                                  <strong>Language:</strong> {tribe.language_family}
                                </p>
                              )}
                              {tribe.cultural_info && (
                                <p className="text-xs text-orange-700 mt-2 italic">
                                  {tribe.cultural_info}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Maternal & Paternal Ancestry (if available) */}
                    {(profile.maternal_line || profile.paternal_line) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {profile.maternal_line && (
                          <div className="border-l-4 border-red-400 pl-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                              <TreePine className="w-5 h-5 mr-2 text-red-600" />
                              Maternal Ancestry
                            </h3>
                            <div className="space-y-2 text-sm">
                              <div className="bg-red-50 border border-red-200 rounded p-3">
                                <p className="text-gray-800">{profile.maternal_line}</p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {profile.paternal_line && (
                          <div className="border-l-4 border-blue-400 pl-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                              <Mountain className="w-5 h-5 mr-2 text-blue-600" />
                              Paternal Ancestry
                            </h3>
                            <div className="space-y-2 text-sm">
                              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                                <p className="text-gray-800">{profile.paternal_line}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Comprehensive Analysis (if available) */}
                    {profile.saved_analysis?.comprehensive_tribal_analysis && (
                      <div className="border-l-4 border-purple-400 pl-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                          <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
                          Comprehensive Analysis
                        </h3>
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                            {profile.saved_analysis.comprehensive_tribal_analysis}
                          </p>
                          {profile.saved_analysis.analysis_date && (
                            <p className="text-xs text-purple-600 mt-4">
                              Analysis completed: {new Date(profile.saved_analysis.analysis_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                  </CardContent>
                </Card>
              );
            })()}
          </TabsContent>
            );
          })()}

          {/* 🧬 3. Family Crest & Photos Section */}
          <TabsContent value="crest" className="space-y-6">
            {(() => {
              const user_role = currentUser?.role || 'user';
              const user_subscription_tier = currentUser?.subscription_tier || 'free';
              
              return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              
              {/* Full Crest Display */}
              <Card className="relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-amber-400 via-orange-400 to-red-400"></div>
                <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
                  <CardTitle className="flex items-center text-xl text-amber-900">
                    <Shield className="w-6 h-6 mr-3" />
                    Family Crest
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center space-y-4">
                  {profile.family_crest ? (
                    <div className="space-y-4">
                      <Dialog>
                        <DialogTrigger asChild>
                          <div className="cursor-pointer group">
                            <img 
                              src={profile.family_crest} 
                              alt="Family Crest" 
                              className="w-48 h-48 mx-auto rounded-lg border-4 border-amber-200 shadow-lg group-hover:border-amber-300 transition-colors"
                            />
                            <p className="text-sm text-amber-600 mt-2 flex items-center justify-center">
                              <ZoomIn className="w-4 h-4 mr-1" />
                              Click to view full size
                            </p>
                          </div>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle className="flex items-center">
                              <Shield className="w-5 h-5 mr-2" />
                              {profile.tribal_name || profile.full_name}'s Family Crest
                            </DialogTitle>
                          </DialogHeader>
                          <div className="text-center space-y-4">
                            <img 
                              src={profile.family_crest} 
                              alt="Family Crest Full View" 
                              className="w-full max-w-md mx-auto rounded-lg border-4 border-amber-200"
                            />
                            <div className="flex gap-3 justify-center">
                              <Button variant="outline" className="flex-1">
                                <Download className="w-4 h-4 mr-2" />
                                Download PNG
                              </Button>
                              <Button variant="outline" className="flex-1">
                                <Download className="w-4 h-4 mr-2" />
                                Download PDF
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                      
                      <div className="flex gap-2 justify-center">
                        <Button variant="outline" size="sm" className="border-amber-300 text-amber-700">
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                        <Button variant="outline" size="sm" className="border-amber-300 text-amber-700">
                          <Share className="w-4 h-4 mr-1" />
                          Share
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="py-12">
                      <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      
                      {/* Check if viewing own profile and is paid user or admin */}
                      {isOwnProfile && (user_role === 'admin' || ['seekers', 'crown'].includes(user_subscription_tier)) ? (
                        <div className="max-w-md mx-auto space-y-4">
                          <p className="text-gray-700 font-medium">No Family Crest Generated Yet</p>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            Create a unique family crest that represents your ancestral heritage, tribal connections, 
                            and family legacy. Our AI-powered crest generator will design a personalized heraldic symbol for you.
                          </p>
                          <Button 
                            onClick={() => window.location.href = '/crest'}
                            className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white"
                          >
                            <Shield className="w-4 h-4 mr-2" />
                            Generate Your Family Crest
                          </Button>
                        </div>
                      ) : (
                        <p className="text-gray-500">No family crest available</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Family Crest Description */}
              {profile.family_crest_description && (
                <Card className="relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-amber-400 via-orange-400 to-red-400"></div>
                  <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
                    <CardTitle className="flex items-center text-xl text-amber-900">
                      <BookOpen className="w-6 h-6 mr-3" />
                      Crest Story & Meaning
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose max-w-none">
                      <p className="text-gray-700 leading-relaxed text-lg">{profile.family_crest_description}</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
              );
            })()}
          </TabsContent>

          {/* 🌌 Cosmic Connection Tab */}
          <TabsContent value="cosmic" className="space-y-6">
            {cosmicConnection ? (
              <div className="space-y-6">
                {/* Big Three Astrology */}
                <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
                  <CardHeader>
                    <CardTitle className="flex items-center text-2xl text-indigo-900">
                      <Sparkles className="w-6 h-6 mr-3 text-purple-600" />
                      The Big Three: Celestial Blueprint
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Sun Sign */}
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-amber-200">
                      <div className="flex items-center mb-3">
                        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mr-4">
                          <span className="text-2xl">☀️</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">Sun Sign</h3>
                          <p className="text-2xl font-bold text-amber-600">{cosmicConnection.sun_sign}</p>
                        </div>
                      </div>
                      {cosmicDetailedExplanations?.sun_detailed ? (
                        <div className="space-y-2">
                          <p className="text-gray-700 text-sm leading-relaxed bg-amber-50 p-3 rounded border-l-4 border-amber-400">
                            <span className="font-semibold text-amber-700">✨ AI Insight:</span> {cosmicDetailedExplanations.sun_detailed}
                          </p>
                        </div>
                      ) : cosmicConnection.sun_interpretation && (
                        <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                          {getInterpretationText(cosmicConnection.sun_interpretation)}
                        </div>
                      )}
                    </div>

                    {/* Moon Sign */}
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-blue-200">
                      <div className="flex items-center mb-3">
                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                          <span className="text-2xl">🌙</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">Moon Sign</h3>
                          <p className="text-2xl font-bold text-blue-600">{cosmicConnection.moon_sign}</p>
                        </div>
                      </div>
                      {cosmicDetailedExplanations?.moon_detailed ? (
                        <div className="space-y-2">
                          <p className="text-gray-700 text-sm leading-relaxed bg-blue-50 p-3 rounded border-l-4 border-blue-400">
                            <span className="font-semibold text-blue-700">✨ AI Insight:</span> {cosmicDetailedExplanations.moon_detailed}
                          </p>
                        </div>
                      ) : cosmicConnection.moon_interpretation && (
                        <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                          {getInterpretationText(cosmicConnection.moon_interpretation)}
                        </div>
                      )}
                    </div>

                    {/* Rising Sign */}
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-purple-200">
                      <div className="flex items-center mb-3">
                        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mr-4">
                          <span className="text-2xl">⬆️</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">Rising Sign (Ascendant)</h3>
                          <p className="text-2xl font-bold text-purple-600">{cosmicConnection.rising_sign}</p>
                        </div>
                      </div>
                      {cosmicDetailedExplanations?.rising_detailed ? (
                        <div className="space-y-2">
                          <p className="text-gray-700 text-sm leading-relaxed bg-purple-50 p-3 rounded border-l-4 border-purple-400">
                            <span className="font-semibold text-purple-700">✨ AI Insight:</span> {cosmicDetailedExplanations.rising_detailed}
                          </p>
                        </div>
                      ) : cosmicConnection.rising_interpretation && (
                        <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                          {getInterpretationText(cosmicConnection.rising_interpretation)}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Numerology Summary */}
                <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                  <CardHeader>
                    <CardTitle className="flex items-center text-2xl text-amber-900">
                      <Star className="w-6 h-6 mr-3 text-orange-600" />
                      Sacred Numbers: The Path of Destiny
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Life Path */}
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-purple-200">
                      <div className="flex items-center mb-3">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mr-4">
                          <span className="text-3xl font-bold text-white">{cosmicConnection.life_path_number}</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">Life Path Number</h3>
                          <p className="text-sm text-gray-600">Your Soul's Journey</p>
                        </div>
                      </div>
                      {cosmicDetailedExplanations?.life_path_detailed ? (
                        <div className="space-y-2">
                          <p className="text-gray-700 text-sm leading-relaxed bg-purple-50 p-3 rounded border-l-4 border-purple-400">
                            <span className="font-semibold text-purple-700">✨ AI Insight:</span> {cosmicDetailedExplanations.life_path_detailed}
                          </p>
                        </div>
                      ) : cosmicConnection.life_path_interpretation && (
                        <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                          {getInterpretationText(cosmicConnection.life_path_interpretation)}
                        </div>
                      )}
                    </div>

                    {/* Expression Number */}
                    {cosmicConnection.expression_number && (
                      <div className="bg-white p-6 rounded-lg shadow-sm border border-blue-200">
                        <div className="flex items-center mb-3">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mr-4">
                            <span className="text-3xl font-bold text-white">{cosmicConnection.expression_number}</span>
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Expression Number</h3>
                            <p className="text-sm text-gray-600">Your Natural Talents</p>
                          </div>
                        </div>
                        {cosmicDetailedExplanations?.expression_detailed ? (
                          <div className="space-y-2">
                            <p className="text-gray-700 text-sm leading-relaxed bg-blue-50 p-3 rounded border-l-4 border-blue-400">
                              <span className="font-semibold text-blue-700">✨ AI Insight:</span> {cosmicDetailedExplanations.expression_detailed}
                            </p>
                          </div>
                        ) : cosmicConnection.expression_interpretation && (
                          <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                            {getInterpretationText(cosmicConnection.expression_interpretation)}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Soul Urge Number */}
                    {cosmicConnection.soul_urge_number && (
                      <div className="bg-white p-6 rounded-lg shadow-sm border border-pink-200">
                        <div className="flex items-center mb-3">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center mr-4">
                            <span className="text-3xl font-bold text-white">{cosmicConnection.soul_urge_number}</span>
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">Soul Urge Number</h3>
                            <p className="text-sm text-gray-600">Your Heart's Desire</p>
                          </div>
                        </div>
                        {cosmicDetailedExplanations?.soul_urge_detailed ? (
                          <div className="space-y-2">
                            <p className="text-gray-700 text-sm leading-relaxed bg-pink-50 p-3 rounded border-l-4 border-pink-400">
                              <span className="font-semibold text-pink-700">✨ AI Insight:</span> {cosmicDetailedExplanations.soul_urge_detailed}
                            </p>
                          </div>
                        ) : cosmicConnection.soul_urge_interpretation && (
                          <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                            {getInterpretationText(cosmicConnection.soul_urge_interpretation)}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Cosmic Wisdom */}
                <Card className="bg-gradient-to-r from-indigo-900 to-purple-900 text-white border-indigo-600">
                  <CardContent className="py-6">
                    <div className="text-center">
                      <Sparkles className="w-12 h-12 mx-auto mb-4 text-amber-300" />
                      <p className="text-lg italic">
                        "The cosmos speaks through numbers and stars, revealing the sacred path each soul must walk."
                      </p>
                      <p className="text-sm text-indigo-200 mt-2">
                        {cosmicConnection.saved_at && `Saved on ${new Date(cosmicConnection.saved_at).toLocaleDateString()}`}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
                <CardContent className="py-12 text-center">
                  <Sparkles className="w-16 h-16 mx-auto mb-4 text-purple-400" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">No Cosmic Connection Yet</h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    {isOwnProfile 
                      ? "Generate your Astrology and Numerology reports from the Cosmic Codes page, then save them to your profile!"
                      : `${profile?.full_name || 'This user'} hasn't shared their Cosmic Connection yet.`
                    }
                  </p>
                  {isOwnProfile && (
                    <Button
                      onClick={() => navigate('/cosmic-codes')}
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Discover Your Cosmic Connection
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

        </Tabs>
      </div>

      {/* Fixed Message & Follow Panel for Mobile */}
      {!isOwnProfile && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 md:hidden">
          <div className="flex gap-3 max-w-sm mx-auto">
            {isAuthenticated ? (
              <>
                <FollowButton
                  userId={userId}
                  initialFollowStatus={isFollowing}
                  size="default"
                  variant="default"
                  onFollowChange={(newStatus) => setIsFollowing(newStatus)}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white border-none"
                />
                <PeacePipeMessage
                  receiverId={userId}
                  receiverName={profile.tribal_name || profile.full_name}
                  trigger={
                    <Button variant="outline" className="flex-1">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Message
                    </Button>
                  }
                />
              </>
            ) : (
              <>
                <Button 
                  variant="default" 
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white border-none"
                  onClick={() => navigate('/')}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Sign In
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => navigate('/')}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Message
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicProfile;